import { useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const REASONS = [
  "Supplier warranty replacement",
  "Supplier replacement for defective or short-shipped units",
  "Free sample from supplier",
];

export const resolveSelectedVendorName = ({
  variant,
  selectedVendor,
  vendorsOptions = [],
  purchaseOrderDetails,
  fallback = "the supplier",
}) => {
  const selectedVendorOption = vendorsOptions.find(
    (vendor) => vendor.value === selectedVendor,
  );

  if (selectedVendorOption?.label) {
    return selectedVendorOption.label;
  }

  if (variant === "edit" && purchaseOrderDetails?.vendorName) {
    return purchaseOrderDetails.vendorName;
  }

  return fallback;
};

const NoChargeConfirmationModal = ({
  lines = [],
  vendorName,
  mode = "create",
  isSubmitting = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const units = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const isOther = reason === "other";

  const confirm = async () => {
    const nextErrors = {
      reason: !reason,
      details: isOther && !details.trim(),
      acknowledged: !acknowledged,
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const reasonText = isOther
      ? details.trim()
      : `${reason}${details.trim() ? `: ${details.trim()}` : ""}`;
    setSubmitting(true);
    try {
      await onConfirm({ reason: reasonText });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/50 p-4" role="presentation">
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="no-charge-title"
      >
        <div className="flex items-start gap-3 px-6 pt-5">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h2 id="no-charge-title" className="text-lg font-semibold text-gray-900">Confirm no-charge items</h2>
            <p className="mt-1 text-sm leading-5 text-gray-700">
              This PO contains items with $0 product cost. Confirm the supplier is providing these items at no charge.
            </p>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">SKU</th>
                  <th className="px-3 py-2.5 font-medium">Product</th>
                  <th className="px-3 py-2.5 font-medium">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium">Unit cost</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={`${line.sku}-${index}`} className="border-t border-gray-200 even:bg-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{line.sku}</td>
                    <td className="px-3 py-2.5 text-gray-700">{line.product_name || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-700">{line.quantity}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-gray-700">$0.0000</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <label htmlFor="no-charge-reason" className="block text-sm font-medium text-gray-900">
              Reason <span className="text-red-600">*</span>
            </label>
            <select
              id="no-charge-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2.5 text-sm ${errors.reason ? "border-red-600 ring-1 ring-red-600" : "border-gray-300"}`}
              aria-invalid={Boolean(errors.reason)}
            >
              <option value="">Select a reason…</option>
              {REASONS.map((option) => <option key={option} value={option}>{option}</option>)}
              <option value="other">Other (add details)</option>
            </select>
            {errors.reason && <p className="mt-1 text-sm text-red-600">Select a reason.</p>}
          </div>

          <div className="mt-4">
            <label htmlFor="no-charge-details" className="block text-sm font-medium text-gray-900">
              Details {!isOther && <span className="font-normal text-gray-500">(optional)</span>}
              {isOther && <span className="text-red-600"> *</span>}
            </label>
            <textarea
              id="no-charge-details"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              placeholder="e.g. Replacing handles from the Spring order defect claim"
              className={`mt-1 block min-h-16 w-full resize-y rounded-lg border px-3 py-2.5 text-sm ${errors.details ? "border-red-600 ring-1 ring-red-600" : "border-gray-300"}`}
              aria-invalid={Boolean(errors.details)}
            />
            {errors.details && <p className="mt-1 text-sm text-red-600">Describe the reason.</p>}
          </div>

          <label className={`mt-4 flex items-start gap-2.5 rounded-lg border p-3 text-sm leading-5 ${errors.acknowledged ? "border-red-600 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              aria-invalid={Boolean(errors.acknowledged)}
            />
            <span>
              I confirm {vendorName || "the supplier"} is providing these {lines.length} SKU(s) ({units} units) at no charge on this PO.
            </span>
          </label>
          {errors.acknowledged && <p className="mt-1 text-sm text-red-600">Check the box to confirm the supplier isn't charging for these items.</p>}

          <p className="mt-3 text-xs text-gray-500">
            Applies to this PO only. Product BOM costs stay unchanged. Your name, the time, the reason and these lines are saved on the PO.
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting || submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={isSubmitting || submitting}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-50"
          >
            {mode === "edit" ? "Confirm no charge & save changes" : "Confirm no charge & create PO"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoChargeConfirmationModal;
